import { useMemo, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/useToast';
import { ApiError } from '@/lib/api/client';
import { profileApi } from '@/lib/api/endpoints';
import { routes } from '@/lib/routes';
import type { CanonicalProfile, SaveState } from '../useCanonicalProfile';
import type { ProfileSectionId } from '../data';

const ORCID_RE = /^\d{4}-\d{4}-\d{4}-\d{3}[\dX]$/i;

function validateOrcid(value: string): string {
  const v = value.trim();
  if (!v) return '';
  return ORCID_RE.test(v) ? '' : 'Формат: 0000-0000-0000-0000';
}

function validateScopus(value: string): string {
  const v = value.trim();
  if (!v) return '';
  return /^\d{4,12}$/.test(v) ? '' : 'Только цифры (4–12)';
}

function formatCitation(
  item: Record<string, unknown>,
  style: 'apa' | 'gost' | 'numbered',
  index: number,
): string {
  const title = String(item.title || 'Без названия');
  const authors = String(item.authors || '—');
  const year = String(item.year || 'n.d.');
  const journal = String(item.journal || '');
  const doi = String(item.doi || '');
  if (style === 'apa') {
    return `${authors} (${year}). ${title}. ${journal}.${doi ? ` https://doi.org/${doi}` : ''}`;
  }
  if (style === 'gost') {
    return `${authors} ${title} // ${journal}. — ${year}.${doi ? ` — DOI: ${doi}` : ''}`;
  }
  return `${index}. ${title} (${year}) — ${journal}${doi ? ` DOI:${doi}` : ''}`;
}

type Props = {
  sectionId: ProfileSectionId;
  profile: CanonicalProfile;
  saveState: SaveState;
  setField: (path: string, value: unknown) => void;
  setSection: (key: string, value: unknown) => void;
  saveNow: () => Promise<boolean>;
};

function Field({
  label,
  value,
  onChange,
  type = 'text',
  readOnly,
  wide,
  error,
  hint,
}: {
  label: string;
  value: string | number;
  onChange?: (v: string) => void;
  type?: string;
  readOnly?: boolean;
  wide?: boolean;
  error?: string;
  hint?: string;
}) {
  return (
    <label className={`profile-field${wide ? ' is-wide' : ''}${error ? ' has-error' : ''}`}>
      <span>{label}</span>
      <input
        type={type}
        value={value ?? ''}
        readOnly={readOnly}
        onChange={(e) => onChange?.(e.target.value)}
        aria-invalid={Boolean(error)}
      />
      {error ? <em className="profile-field-error">{error}</em> : null}
      {!error && hint ? <small className="profile-field-hint">{hint}</small> : null}
    </label>
  );
}

function TextArea({
  label,
  value,
  onChange,
  wide,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  wide?: boolean;
}) {
  return (
    <label className={`profile-field${wide ? ' is-wide' : ''}`}>
      <span>{label}</span>
      <textarea value={value ?? ''} onChange={(e) => onChange(e.target.value)} rows={3} />
    </label>
  );
}

function EmptyState({ title, text }: { title: string; text: string }) {
  return (
    <div className="profile-empty">
      <b>{title}</b>
      <span>{text}</span>
    </div>
  );
}

function AccountPane({ profile, setField, saveNow }: Props) {
  const navigate = useNavigate();
  const account = (profile.account || {}) as Record<string, string | boolean>;
  const privacy = (profile.privacy || {}) as Record<string, boolean>;
  return (
    <div className="profile-account">
      <div className="profile-info-grid">
        <article className="profile-info-card" style={{ '--a': '#41d2ff' } as CSSProperties}>
          <h3>Academia ID</h3>
          <b className="profile-info-big">{String(account.academiaId || '—')}</b>
          <p>Один профиль для всех разрешённых ролей.</p>
        </article>
        <article className="profile-info-card" style={{ '--a': '#49ddb7' } as CSSProperties}>
          <h3>Состояние аккаунта</h3>
          <b className="profile-info-big">Активен</b>
          <p>Изменения сохраняются автоматически. Важные изменения нужно подтвердить.</p>
        </article>
      </div>
      <div className="profile-form-title">
        <h3>Контакты и интерфейс</h3>
        <small>Изменения сохраняются автоматически</small>
      </div>
      <div className="profile-form-grid">
        <Field label="Academia ID" value={String(account.academiaId || '')} readOnly />
        <Field
          label="Основной email"
          type="email"
          value={String(account.email || '')}
          onChange={(v) => setField('account.email', v)}
        />
        <Field
          label="Дополнительный email"
          type="email"
          value={String(account.altEmail || '')}
          onChange={(v) => setField('account.altEmail', v)}
        />
        <Field
          label="Телефон"
          type="tel"
          value={String(account.phone || '')}
          onChange={(v) => setField('account.phone', v)}
        />
        <label className="profile-field">
          <span>Язык интерфейса</span>
          <select
            value={String(account.language || 'RU')}
            onChange={(e) => setField('account.language', e.target.value)}
          >
            <option value="RU">RU</option>
            <option value="KZ">KZ</option>
            <option value="EN">EN</option>
          </select>
        </label>
        <label className="profile-check">
          <input
            type="checkbox"
            checked={privacy.online !== false}
            onChange={(e) => setField('privacy.online', e.target.checked)}
          />
          <span>Показывать, что я в сети</span>
        </label>
      </div>
      <section className="profile-security-grid">
        <article>
          <span aria-hidden="true">🔐</span>
          <div>
            <h3>Пароль и вход</h3>
            <p>Смена пароля, активные сессии, завершение чужих сессий.</p>
          </div>
          <button type="button" onClick={() => navigate(routes.security)}>
            Управлять
          </button>
        </article>
        <article>
          <span aria-hidden="true">✉</span>
          <div>
            <h3>Подтверждение контактов</h3>
            <p>Для изменения почты или телефона подтвердите новый контакт.</p>
          </div>
          <button type="button" onClick={() => void saveNow()}>
            Проверить
          </button>
        </article>
        <article>
          <span aria-hidden="true">🛡</span>
          <div>
            <h3>Двухэтапная защита</h3>
            <p>Authenticator / резервные коды / доверенные устройства.</p>
          </div>
          <button type="button" onClick={() => navigate(routes.security)}>
            Настроить 2FA
          </button>
        </article>
      </section>
    </div>
  );
}

function PersonalPane({ profile, setField }: Props) {
  const personal = profile.personal || {};
  const kz = personal.kz || {};
  const ru = personal.ru || {};
  const en = personal.en || {};
  const initials = `${String(ru.given || 'С').slice(0, 1)}${String(ru.family || 'Ж').slice(0, 1)}`;
  const rotation = Number((personal as { avatarRotation?: number }).avatarRotation || 0);
  const zoom = Number((personal as { avatarZoom?: number }).avatarZoom || 1);

  return (
    <div>
      <div className="profile-language-hero">
        <article style={{ '--a': '#41d2ff' } as CSSProperties}>
          <span className="profile-flag">KZ</span>
          <h3>Қазақша</h3>
          <p>Официальное написание ФИО</p>
          <Field label="Аты" value={kz.given || ''} onChange={(v) => setField('personal.kz.given', v)} />
          <Field label="Тегі" value={kz.family || ''} onChange={(v) => setField('personal.kz.family', v)} />
          <Field
            label="Әкесінің аты"
            value={kz.middle || ''}
            onChange={(v) => setField('personal.kz.middle', v)}
          />
        </article>
        <article style={{ '--a': '#ff8b75' } as CSSProperties}>
          <span className="profile-flag">RU</span>
          <h3>Русский</h3>
          <p>ФИО для русскоязычных документов</p>
          <Field label="Имя" value={ru.given || ''} onChange={(v) => setField('personal.ru.given', v)} />
          <Field
            label="Фамилия"
            value={ru.family || ''}
            onChange={(v) => setField('personal.ru.family', v)}
          />
          <Field
            label="Отчество"
            value={ru.middle || ''}
            onChange={(v) => setField('personal.ru.middle', v)}
          />
        </article>
        <article style={{ '--a': '#9e80ff' } as CSSProperties}>
          <span className="profile-flag">EN</span>
          <h3>English</h3>
          <p>Name for publications and international profiles</p>
          <Field
            label="Given name"
            value={en.given || ''}
            onChange={(v) => setField('personal.en.given', v)}
          />
          <Field
            label="Family name"
            value={en.family || ''}
            onChange={(v) => setField('personal.en.family', v)}
          />
          <Field
            label="Middle name"
            value={en.middle || ''}
            onChange={(v) => setField('personal.en.middle', v)}
          />
        </article>
      </div>
      <div className="profile-form-grid" style={{ marginTop: 14 }}>
        <article className="profile-side-card">
          <h3>Avatar</h3>
          <div className="profile-avatar-preview" aria-hidden="true">
            {personal.avatarUrl ? (
              <img
                src={personal.avatarUrl}
                alt=""
                style={{ transform: `rotate(${rotation}deg) scale(${zoom})` }}
              />
            ) : (
              initials
            )}
          </div>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              const reader = new FileReader();
              reader.onload = () => setField('personal.avatarUrl', String(reader.result || ''));
              reader.readAsDataURL(file);
            }}
          />
          <label className="profile-avatar-control">
            <span>Поворот · {rotation}°</span>
            <input
              type="range"
              min={0}
              max={360}
              step={1}
              value={rotation}
              onChange={(e) => setField('personal.avatarRotation', Number(e.target.value))}
            />
          </label>
          <label className="profile-avatar-control">
            <span>Масштаб · {zoom.toFixed(2)}×</span>
            <input
              type="range"
              min={1}
              max={2}
              step={0.05}
              value={zoom}
              onChange={(e) => setField('personal.avatarZoom', Number(e.target.value))}
            />
          </label>
          <div className="profile-inline-actions">
            <button
              type="button"
              onClick={() => {
                setField('personal.avatarUrl', '');
                setField('personal.avatarRotation', 0);
                setField('personal.avatarZoom', 1);
              }}
            >
              Удалить
            </button>
          </div>
        </article>
        <article className="profile-side-card">
          <h3>Локация</h3>
          <Field
            label="Страна"
            value={personal.country || ''}
            onChange={(v) => setField('personal.country', v)}
          />
          <Field
            label="Город"
            value={personal.city || ''}
            onChange={(v) => setField('personal.city', v)}
          />
          <Field
            label="Часовой пояс"
            value={personal.timezone || ''}
            onChange={(v) => setField('personal.timezone', v)}
          />
        </article>
      </div>
    </div>
  );
}

function AcademicPane({ profile, setField, saveNow }: Props) {
  const { notify } = useToast();
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const academic = (profile.academic || {}) as Record<string, string>;
  const provenance = ((profile.academic as { idProvenance?: Record<string, Record<string, string>> })
    ?.idProvenance || {}) as Record<string, Record<string, string>>;
  const ids = [
    { key: 'orcid', name: 'ORCID', accent: '#a6ce39', ph: '0000-0000-0000-0000', logo: 'iD' },
    { key: 'scopus', name: 'Scopus', accent: '#ff8b30', ph: 'Author ID', logo: 'Sc' },
    { key: 'scholar', name: 'Google Scholar', accent: '#4285f4', ph: 'Profile URL', logo: 'G' },
    { key: 'wos', name: 'Web of Science', accent: '#9e80ff', ph: 'ResearcherID', logo: 'W' },
  ] as const;

  const orcidError = validateOrcid(academic.orcid || '');
  const scopusError = validateScopus(academic.scopus || '');

  const verifyId = async (key: string, force = false) => {
    if (key === 'orcid' && orcidError) {
      notify(orcidError, 'error');
      return;
    }
    if (key === 'scopus' && scopusError) {
      notify(scopusError, 'error');
      return;
    }
    setBusyKey(key);
    try {
      await saveNow();
      const result = await profileApi.verifyId(key, force);
      const entry = result.provenance;
      setField(`academic.idProvenance.${key}`, entry);
      notify(
        result.status === 'already_confirmed'
          ? `${key.toUpperCase()}: уже подтверждён, значение не перезаписано`
          : `${key.toUpperCase()}: ${result.detail}`,
        'success',
      );
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Проверка не удалась';
      notify(message, 'error');
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div>
      <section className="profile-orcid-hero">
        <div>
          <span className="profile-orcid-logo">iD</span>
          <h3>ORCID Integration</h3>
          <p>
            Свяжите профиль с ORCID. Проверка фиксирует provenance и не затирает подтверждённые
            данные без явного действия.
          </p>
        </div>
        <button
          type="button"
          className="profile-btn primary"
          disabled={busyKey === 'orcid'}
          onClick={() => void verifyId('orcid')}
        >
          {busyKey === 'orcid' ? 'Проверка…' : 'Подключить ORCID'}
        </button>
      </section>
      <div className="profile-id-grid">
        {ids.map((item) => {
          const prov = provenance[item.key] || {};
          const stateLabel = prov.status
            ? `${prov.status}${prov.checked_at ? ` · ${String(prov.checked_at).slice(0, 10)}` : ''}${
                prov.source ? ` · ${prov.source}` : ''
              }`
            : academic[item.key]
              ? 'Указан · доступна проверка'
              : 'Не подключён';
          const fieldError =
            item.key === 'orcid' ? orcidError : item.key === 'scopus' ? scopusError : '';
          return (
            <article key={item.key} style={{ '--a': item.accent } as CSSProperties}>
              <span className="profile-id-logo">{item.logo}</span>
              <div>
                <small>НАУЧНЫЙ ИДЕНТИФИКАТОР</small>
                <h3>{item.name}</h3>
                <input
                  value={academic[item.key] || ''}
                  placeholder={item.ph}
                  aria-invalid={Boolean(fieldError)}
                  onChange={(e) => setField(`academic.${item.key}`, e.target.value)}
                />
                {fieldError ? <em className="profile-field-error">{fieldError}</em> : null}
                <span className="profile-id-state">{stateLabel}</span>
              </div>
              <div className="profile-id-actions">
                <button
                  type="button"
                  disabled={busyKey === item.key}
                  onClick={() => void verifyId(item.key)}
                >
                  {busyKey === item.key ? '…' : 'Проверить'}
                </button>
                {prov.status === 'confirmed' || prov.status === 'verified' ? (
                  <button type="button" onClick={() => void verifyId(item.key, true)}>
                    Перезаписать
                  </button>
                ) : null}
              </div>
            </article>
          );
        })}
      </div>
      <div className="profile-form-title">
        <h3>Статус и научная экспертиза</h3>
        <small>Используется в CV и авторском профиле</small>
      </div>
      <div className="profile-form-grid">
        <Field
          label="Статус"
          value={academic.status || ''}
          onChange={(v) => setField('academic.status', v)}
        />
        <Field
          label="Научная степень"
          value={academic.degree || ''}
          onChange={(v) => setField('academic.degree', v)}
        />
        <Field
          label="Звание"
          value={academic.title || ''}
          onChange={(v) => setField('academic.title', v)}
        />
        <Field
          label="Область науки"
          value={academic.scienceField || ''}
          onChange={(v) => setField('academic.scienceField', v)}
        />
        <Field
          label="Ключевые слова"
          value={academic.keywords || ''}
          onChange={(v) => setField('academic.keywords', v)}
          wide
        />
        <Field
          label="Интересы"
          value={academic.interests || ''}
          onChange={(v) => setField('academic.interests', v)}
          wide
        />
        <Field
          label="Методы"
          value={academic.methods || ''}
          onChange={(v) => setField('academic.methods', v)}
          wide
        />
      </div>
    </div>
  );
}

function updateItem(
  list: Array<Record<string, unknown>>,
  index: number,
  key: string,
  value: unknown,
) {
  return list.map((item, i) => (i === index ? { ...item, [key]: value } : item));
}

function EducationPane({ profile, setSection, saveNow }: Props) {
  const items = useMemo(
    () => (profile.education || []).filter((x) => !x.archived),
    [profile.education],
  );
  const raw = profile.education || [];

  const add = () => {
    setSection('education', [
      ...raw,
      {
        level: '',
        institution: '',
        program: '',
        diplomaNumber: '',
        country: 'Казахстан',
        city: '',
        start: '',
        end: '',
        note: '',
      },
    ]);
  };

  return (
    <div>
      <div className="profile-addbar">
        <div>
          <h3>История образования</h3>
          <p>Каждая запись: вуз, программа, годы, диплом, страна и город.</p>
        </div>
        <button type="button" className="profile-btn primary" onClick={add}>
          ＋ Добавить образование
        </button>
      </div>
      <div className="profile-edit-list">
        {!items.length ? (
          <EmptyState
            title="Пока нет записей"
            text="Нажмите «Добавить образование» — откроется редактируемая карточка."
          />
        ) : (
          raw.map((x, i) =>
            x.archived ? null : (
              <article key={i} className="profile-edit-card" style={{ '--a': '#e2ad3a' } as CSSProperties}>
                <header>
                  <div className="profile-edit-icon">EDU</div>
                  <div>
                    <small>ОБРАЗОВАНИЕ · {String(i + 1).padStart(2, '0')}</small>
                    <h3>{String(x.institution || x.level || 'Новая запись об образовании')}</h3>
                  </div>
                  <div className="profile-edit-actions">
                    <button type="button" onClick={() => void saveNow()}>
                      Сохранить
                    </button>
                    <button
                      type="button"
                      className="danger"
                      onClick={() => setSection('education', updateItem(raw, i, 'archived', true))}
                    >
                      Архивировать
                    </button>
                  </div>
                </header>
                <div className="profile-edit-grid">
                  <Field
                    label="Уровень / степень"
                    value={String(x.level || '')}
                    onChange={(v) => setSection('education', updateItem(raw, i, 'level', v))}
                  />
                  <Field
                    label="Название вуза"
                    value={String(x.institution || '')}
                    onChange={(v) => setSection('education', updateItem(raw, i, 'institution', v))}
                  />
                  <Field
                    label="Специальность / образовательная программа"
                    value={String(x.program || '')}
                    onChange={(v) => setSection('education', updateItem(raw, i, 'program', v))}
                    wide
                  />
                  <Field
                    label="Номер диплома"
                    value={String(x.diplomaNumber || '')}
                    onChange={(v) => setSection('education', updateItem(raw, i, 'diplomaNumber', v))}
                  />
                  <Field
                    label="Страна"
                    value={String(x.country || '')}
                    onChange={(v) => setSection('education', updateItem(raw, i, 'country', v))}
                  />
                  <Field
                    label="Город"
                    value={String(x.city || '')}
                    onChange={(v) => setSection('education', updateItem(raw, i, 'city', v))}
                  />
                  <Field
                    label="Год поступления"
                    type="number"
                    value={String(x.start || '')}
                    onChange={(v) => setSection('education', updateItem(raw, i, 'start', v))}
                  />
                  <Field
                    label="Год окончания"
                    type="number"
                    value={String(x.end || '')}
                    onChange={(v) => setSection('education', updateItem(raw, i, 'end', v))}
                  />
                  <TextArea
                    label="Дополнительные сведения"
                    value={String(x.note || '')}
                    onChange={(v) => setSection('education', updateItem(raw, i, 'note', v))}
                    wide
                  />
                </div>
              </article>
            ),
          )
        )}
      </div>
    </div>
  );
}

function EmploymentPane({ profile, setSection, saveNow }: Props) {
  const raw = profile.work || [];
  const add = () =>
    setSection('work', [
      ...raw,
      {
        organization: '',
        department: '',
        position: '',
        city: '',
        start: '',
        end: '',
        current: false,
        responsibilities: '',
      },
    ]);

  return (
    <div>
      <div className="profile-addbar">
        <div>
          <h3>Трудовая деятельность</h3>
          <p>Организация, кафедра, должность, период и обязанности.</p>
        </div>
        <button type="button" className="profile-btn primary" onClick={add}>
          ＋ Добавить место работы
        </button>
      </div>
      <div className="profile-edit-list">
        {!raw.filter((x) => !x.archived).length ? (
          <EmptyState title="Пока нет записей" text="Добавьте текущую или прошлую организацию." />
        ) : (
          raw.map((x, i) =>
            x.archived ? null : (
              <article key={i} className="profile-edit-card" style={{ '--a': '#ff8b75' } as CSSProperties}>
                <header>
                  <div className="profile-edit-icon">JOB</div>
                  <div>
                    <small>МЕСТО РАБОТЫ · {String(i + 1).padStart(2, '0')}</small>
                    <h3>{String(x.organization || 'Новое место работы')}</h3>
                  </div>
                  <div className="profile-edit-actions">
                    <button type="button" onClick={() => void saveNow()}>
                      Сохранить
                    </button>
                    <button
                      type="button"
                      className="danger"
                      onClick={() => setSection('work', updateItem(raw, i, 'archived', true))}
                    >
                      Архивировать
                    </button>
                  </div>
                </header>
                <div className="profile-edit-grid">
                  <Field
                    label="Организация"
                    value={String(x.organization || '')}
                    onChange={(v) => setSection('work', updateItem(raw, i, 'organization', v))}
                  />
                  <Field
                    label="Подразделение / кафедра"
                    value={String(x.department || '')}
                    onChange={(v) => setSection('work', updateItem(raw, i, 'department', v))}
                  />
                  <Field
                    label="Должность"
                    value={String(x.position || '')}
                    onChange={(v) => setSection('work', updateItem(raw, i, 'position', v))}
                  />
                  <Field
                    label="Город"
                    value={String(x.city || '')}
                    onChange={(v) => setSection('work', updateItem(raw, i, 'city', v))}
                  />
                  <Field
                    label="Дата начала"
                    type="date"
                    value={String(x.start || '')}
                    onChange={(v) => setSection('work', updateItem(raw, i, 'start', v))}
                  />
                  <Field
                    label="Дата окончания"
                    type="date"
                    value={String(x.end || '')}
                    onChange={(v) => setSection('work', updateItem(raw, i, 'end', v))}
                  />
                  <label className="profile-check">
                    <input
                      type="checkbox"
                      checked={Boolean(x.current)}
                      onChange={(e) => setSection('work', updateItem(raw, i, 'current', e.target.checked))}
                    />
                    <span>Работаю здесь сейчас</span>
                  </label>
                  <TextArea
                    label="Основные обязанности / научная деятельность"
                    value={String(x.responsibilities || '')}
                    onChange={(v) => setSection('work', updateItem(raw, i, 'responsibilities', v))}
                    wide
                  />
                </div>
              </article>
            ),
          )
        )}
      </div>
    </div>
  );
}

function PublicationsPane({ profile, setSection, saveNow }: Props) {
  const { notify } = useToast();
  const [query, setQuery] = useState('');
  const [hint, setHint] = useState(
    'Введите DOI или название — candidate для ручного подтверждения.',
  );
  const [yearFrom, setYearFrom] = useState('');
  const [yearTo, setYearTo] = useState('');
  const [indexingFilter, setIndexingFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [langFilter, setLangFilter] = useState('');
  const [citeStyle, setCiteStyle] = useState<'numbered' | 'apa' | 'gost'>('numbered');
  const raw = profile.publications || [];

  const filtered = useMemo(() => {
    return raw
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => {
        const year = Number(item.year || 0);
        if (yearFrom && (!year || year < Number(yearFrom))) return false;
        if (yearTo && (!year || year > Number(yearTo))) return false;
        if (
          indexingFilter &&
          !String(item.indexing || '')
            .toLowerCase()
            .includes(indexingFilter.toLowerCase())
        ) {
          return false;
        }
        if (
          typeFilter &&
          !String(item.type || item.indexing || '')
            .toLowerCase()
            .includes(typeFilter.toLowerCase())
        ) {
          return false;
        }
        if (
          langFilter &&
          !String(item.language || '')
            .toLowerCase()
            .includes(langFilter.toLowerCase())
        ) {
          return false;
        }
        return true;
      });
  }, [raw, yearFrom, yearTo, indexingFilter, typeFilter, langFilter]);

  const addManual = (seed?: Partial<Record<string, string>>) => {
    setSection('publications', [
      ...raw,
      {
        title: seed?.title || '',
        journal: seed?.journal || '',
        year: seed?.year || '',
        doi: seed?.doi || '',
        url: seed?.url || '',
        indexing: seed?.indexing || '',
        type: seed?.type || '',
        language: seed?.language || '',
        authors: seed?.authors || '',
        note: '',
      },
    ]);
  };

  const copyCitation = async (item: Record<string, unknown>, index: number) => {
    const text = formatCitation(item, citeStyle, index + 1);
    try {
      await navigator.clipboard.writeText(text);
      notify(`Цитата (${citeStyle.toUpperCase()}) скопирована`, 'success');
    } catch {
      notify(text, 'info');
    }
  };

  return (
    <div>
      <section className="profile-import-hero">
        <div>
          <small>Публикации</small>
          <h3>Добавить публикацию</h3>
          <p>Поиск DOI/title/URL и импорт-кандидат из ORCID / Google Scholar.</p>
        </div>
        <div className="profile-import-row">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="DOI, название статьи или URL"
          />
          <button
            type="button"
            onClick={() => {
              if (!query.trim()) {
                setHint('Введите DOI или название.');
                return;
              }
              setHint(`Candidate: «${query.trim()}» — подтвердите и сохраните карточку.`);
              addManual({
                title: query.trim(),
                doi: query.includes('10.') ? query.trim() : '',
                indexing: 'кандидат',
              });
            }}
          >
            Найти
          </button>
          <button
            type="button"
            onClick={() => {
              notify('ORCID import: добавлен candidate', 'info');
              addManual({ title: 'ORCID candidate', indexing: 'ORCID import' });
            }}
          >
            ORCID
          </button>
          <button
            type="button"
            onClick={() => {
              notify('Scholar import: добавлен candidate', 'info');
              addManual({ title: 'Scholar candidate', indexing: 'Scholar import' });
            }}
          >
            Google Scholar
          </button>
          <button type="button" className="profile-btn primary" onClick={() => addManual()}>
            ＋ Вручную
          </button>
        </div>
        <div className="profile-inline-status">{hint}</div>
      </section>
      <div className="profile-works-filters">
        <label>
          <span>Год с</span>
          <input value={yearFrom} onChange={(e) => setYearFrom(e.target.value)} placeholder="2020" />
        </label>
        <label>
          <span>Год по</span>
          <input value={yearTo} onChange={(e) => setYearTo(e.target.value)} placeholder="2026" />
        </label>
        <label>
          <span>Индексация</span>
          <input
            value={indexingFilter}
            onChange={(e) => setIndexingFilter(e.target.value)}
            placeholder="Scopus / WoS"
          />
        </label>
        <label>
          <span>Тип</span>
          <input
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            placeholder="статья / монография"
          />
        </label>
        <label>
          <span>Язык</span>
          <input
            value={langFilter}
            onChange={(e) => setLangFilter(e.target.value)}
            placeholder="ru / en / kz"
          />
        </label>
        <label>
          <span>Стиль цитирования</span>
          <select
            value={citeStyle}
            onChange={(e) => setCiteStyle(e.target.value as 'numbered' | 'apa' | 'gost')}
          >
            <option value="numbered">Нумерованный</option>
            <option value="apa">APA</option>
            <option value="gost">ГОСТ</option>
          </select>
        </label>
      </div>
      <div className="profile-edit-list">
        {!filtered.length ? (
          <EmptyState
            title={raw.length ? 'Нет совпадений по фильтрам' : 'Публикаций пока нет'}
            text={
              raw.length
                ? 'Сбросьте фильтры или измените критерии.'
                : 'Используйте поиск DOI или добавьте вручную.'
            }
          />
        ) : (
          filtered.map(({ item: x, index: i }) => (
            <article key={i} className="profile-edit-card" style={{ '--a': '#9e80ff' } as CSSProperties}>
              <header>
                <div className="profile-edit-icon">PUB</div>
                <div>
                  <small>ПУБЛИКАЦИЯ · {String(i + 1).padStart(2, '0')}</small>
                  <h3>{String(x.title || 'Новая публикация')}</h3>
                  <p>{String(x.journal || 'Журнал не указан')}</p>
                </div>
                <div className="profile-edit-actions">
                  <button type="button" onClick={() => void copyCitation(x, i)}>
                    Цитата
                  </button>
                  <button type="button" onClick={() => void saveNow()}>
                    Сохранить
                  </button>
                </div>
              </header>
              <div className="profile-edit-grid">
                {(
                  [
                    ['title', 'Название', 'text', true],
                    ['journal', 'Журнал / издатель', 'text', false],
                    ['year', 'Год', 'number', false],
                    ['doi', 'DOI', 'text', false],
                    ['url', 'URL', 'url', false],
                    ['indexing', 'Индексация', 'text', false],
                    ['type', 'Тип', 'text', false],
                    ['language', 'Язык', 'text', false],
                    ['authors', 'Авторы', 'text', true],
                  ] as const
                ).map(([key, label, type, wide]) => (
                  <Field
                    key={key}
                    label={label}
                    type={type}
                    wide={wide}
                    value={String(x[key] || '')}
                    onChange={(v) => setSection('publications', updateItem(raw, i, key, v))}
                  />
                ))}
                <TextArea
                  label="Примечание"
                  value={String(x.note || '')}
                  onChange={(v) => setSection('publications', updateItem(raw, i, 'note', v))}
                  wide
                />
              </div>
            </article>
          ))
        )}
      </div>
    </div>
  );
}

function ProjectsPane({ profile, setSection, saveNow }: Props) {
  const raw = profile.projects || [];
  const add = () =>
    setSection('projects', [
      ...raw,
      {
        title: '',
        number: '',
        role: '',
        funder: '',
        organization: '',
        start: '',
        end: '',
        budget: '',
        results: '',
      },
    ]);

  return (
    <div>
      <div className="profile-addbar">
        <div>
          <h3>Проекты и гранты</h3>
          <p>Финансирование, роль, организация, сроки, номер и результаты.</p>
        </div>
        <button type="button" className="profile-btn primary" onClick={add}>
          ＋ Добавить проект / грант
        </button>
      </div>
      <div className="profile-edit-list">
        {!raw.length ? (
          <EmptyState title="Проектов пока нет" text="Добавьте грант или исследовательский проект." />
        ) : (
          raw.map((x, i) => (
            <article key={i} className="profile-edit-card" style={{ '--a': '#49ddb7' } as CSSProperties}>
              <header>
                <div className="profile-edit-icon">GR</div>
                <div>
                  <small>ПРОЕКТ / ГРАНТ · {String(i + 1).padStart(2, '0')}</small>
                  <h3>{String(x.title || 'Новый проект')}</h3>
                </div>
                <div className="profile-edit-actions">
                  <button type="button" onClick={() => void saveNow()}>
                    Сохранить
                  </button>
                </div>
              </header>
              <div className="profile-edit-grid">
                {(
                  [
                    ['title', 'Название проекта', true],
                    ['number', 'Номер / шифр', false],
                    ['role', 'Роль', false],
                    ['funder', 'Источник финансирования', false],
                    ['organization', 'Организация', false],
                    ['start', 'Начало', false],
                    ['end', 'Окончание', false],
                    ['budget', 'Бюджет', false],
                  ] as const
                ).map(([key, label, wide]) => (
                  <Field
                    key={key}
                    label={label}
                    wide={wide}
                    type={key === 'start' || key === 'end' ? 'date' : 'text'}
                    value={String(x[key] || '')}
                    onChange={(v) => setSection('projects', updateItem(raw, i, key, v))}
                  />
                ))}
                <TextArea
                  label="Основные результаты"
                  value={String(x.results || '')}
                  onChange={(v) => setSection('projects', updateItem(raw, i, 'results', v))}
                  wide
                />
              </div>
            </article>
          ))
        )}
      </div>
    </div>
  );
}

const ACH_TYPES = [
  'Награда',
  'Сертификат',
  'Диплом',
  'Курс',
  'Монография',
  'Словарь',
  'Авторское свидетельство',
  'Научное достижение',
];

function AchievementsPane({ profile, setSection, saveNow }: Props) {
  const raw = profile.achievements || [];
  const add = (type = '') =>
    setSection('achievements', [
      ...raw,
      { type, title: '', organization: '', year: '', number: '', url: '', description: '' },
    ]);

  return (
    <div>
      <div className="profile-addbar">
        <div>
          <h3>Научные и профессиональные достижения</h3>
          <p>Награды, сертификаты, монографии, словари и авторские свидетельства.</p>
        </div>
        <button type="button" className="profile-btn primary" onClick={() => add()}>
          ＋ Добавить достижение
        </button>
      </div>
      <div className="profile-ach-types">
        {ACH_TYPES.map((type) => (
          <button key={type} type="button" onClick={() => add(type)}>
            {type}
          </button>
        ))}
      </div>
      <div className="profile-edit-list">
        {!raw.length ? (
          <EmptyState title="Достижений пока нет" text="Выберите быстрый тип или добавьте вручную." />
        ) : (
          raw.map((x, i) => (
            <article key={i} className="profile-edit-card" style={{ '--a': '#e2ad3a' } as CSSProperties}>
              <header>
                <div className="profile-edit-icon">★</div>
                <div>
                  <small>
                    {String(x.type || 'ДОСТИЖЕНИЕ')} · {String(i + 1).padStart(2, '0')}
                  </small>
                  <h3>{String(x.title || 'Новое достижение')}</h3>
                </div>
                <div className="profile-edit-actions">
                  <button type="button" onClick={() => void saveNow()}>
                    Сохранить
                  </button>
                </div>
              </header>
              <div className="profile-edit-grid">
                <Field
                  label="Тип"
                  value={String(x.type || '')}
                  onChange={(v) => setSection('achievements', updateItem(raw, i, 'type', v))}
                />
                <Field
                  label="Название"
                  value={String(x.title || '')}
                  onChange={(v) => setSection('achievements', updateItem(raw, i, 'title', v))}
                  wide
                />
                <Field
                  label="Организация / издательство"
                  value={String(x.organization || '')}
                  onChange={(v) => setSection('achievements', updateItem(raw, i, 'organization', v))}
                />
                <Field
                  label="Год"
                  type="number"
                  value={String(x.year || '')}
                  onChange={(v) => setSection('achievements', updateItem(raw, i, 'year', v))}
                />
                <Field
                  label="Номер / ISBN / свидетельство"
                  value={String(x.number || '')}
                  onChange={(v) => setSection('achievements', updateItem(raw, i, 'number', v))}
                />
                <Field
                  label="Ссылка"
                  type="url"
                  value={String(x.url || '')}
                  onChange={(v) => setSection('achievements', updateItem(raw, i, 'url', v))}
                />
                <TextArea
                  label="Описание / результат"
                  value={String(x.description || '')}
                  onChange={(v) => setSection('achievements', updateItem(raw, i, 'description', v))}
                  wide
                />
              </div>
            </article>
          ))
        )}
      </div>
    </div>
  );
}

function DocumentsPane({ profile, setSection, saveNow }: Props) {
  const raw = profile.documents || [];
  const [preview, setPreview] = useState<Record<string, unknown> | null>(null);
  const add = () =>
    setSection('documents', [
      ...raw,
      {
        title: '',
        type: '',
        number: '',
        date: '',
        issuer: '',
        link: '',
        description: '',
        linkedTo: '',
      },
    ]);

  const safeLink = String(preview?.link || '');
  const isHttp = /^https?:\/\//i.test(safeLink);
  const isDataImage = /^data:image\//i.test(safeLink);

  return (
    <div>
      <div className="profile-addbar">
        <div>
          <h3>Документы профиля</h3>
          <p>Semantic registry + Safe preview. Можно связать с Education / Project / Publication / Completion.</p>
        </div>
        <button type="button" className="profile-btn primary" onClick={add}>
          ＋ Добавить документ
        </button>
      </div>
      <div className="profile-edit-list">
        {!raw.length ? (
          <EmptyState title="Документов пока нет" text="Добавьте запись и укажите связь с разделом профиля." />
        ) : (
          raw.map((x, i) => (
            <article key={i} className="profile-edit-card" style={{ '--a': '#41d2ff' } as CSSProperties}>
              <header>
                <div className="profile-edit-icon">DOC</div>
                <div>
                  <small>ДОКУМЕНТ · {String(i + 1).padStart(2, '0')}</small>
                  <h3>{String(x.title || 'Новый документ')}</h3>
                </div>
                <div className="profile-edit-actions">
                  <button type="button" onClick={() => setPreview(x)}>
                    Safe preview
                  </button>
                  <button type="button" onClick={() => void saveNow()}>
                    Сохранить
                  </button>
                </div>
              </header>
              <div className="profile-edit-grid">
                <Field
                  label="Название документа"
                  value={String(x.title || '')}
                  onChange={(v) => setSection('documents', updateItem(raw, i, 'title', v))}
                  wide
                />
                <Field
                  label="Тип"
                  value={String(x.type || '')}
                  onChange={(v) => setSection('documents', updateItem(raw, i, 'type', v))}
                />
                <Field
                  label="Номер / серия"
                  value={String(x.number || '')}
                  onChange={(v) => setSection('documents', updateItem(raw, i, 'number', v))}
                />
                <Field
                  label="Дата"
                  type="date"
                  value={String(x.date || '')}
                  onChange={(v) => setSection('documents', updateItem(raw, i, 'date', v))}
                />
                <Field
                  label="Кем выдан / организация"
                  value={String(x.issuer || '')}
                  onChange={(v) => setSection('documents', updateItem(raw, i, 'issuer', v))}
                />
                <Field
                  label="Ссылка / File Hub reference"
                  value={String(x.link || '')}
                  onChange={(v) => setSection('documents', updateItem(raw, i, 'link', v))}
                />
                <label className="profile-field">
                  <span>Связь с записью профиля</span>
                  <select
                    value={String(x.linkedTo || '')}
                    onChange={(e) => setSection('documents', updateItem(raw, i, 'linkedTo', e.target.value))}
                  >
                    <option value="">—</option>
                    <option value="education">Education</option>
                    <option value="project">Project</option>
                    <option value="publication">Publication</option>
                    <option value="completion">Completion</option>
                  </select>
                </label>
                <TextArea
                  label="Описание"
                  value={String(x.description || '')}
                  onChange={(v) => setSection('documents', updateItem(raw, i, 'description', v))}
                  wide
                />
              </div>
            </article>
          ))
        )}
      </div>
      {preview ? (
        <div
          className="profile-safe-preview"
          role="dialog"
          aria-modal="true"
          aria-label="Safe preview документа"
        >
          <div className="profile-safe-preview-card">
            <header>
              <div>
                <small>Предпросмотр</small>
                <h3>{String(preview.title || 'Документ')}</h3>
              </div>
              <button type="button" onClick={() => setPreview(null)}>
                Закрыть ×
              </button>
            </header>
            <dl>
              <div>
                <dt>Тип</dt>
                <dd>{String(preview.type || '—')}</dd>
              </div>
              <div>
                <dt>Номер</dt>
                <dd>{String(preview.number || '—')}</dd>
              </div>
              <div>
                <dt>Дата</dt>
                <dd>{String(preview.date || '—')}</dd>
              </div>
              <div>
                <dt>Выдан</dt>
                <dd>{String(preview.issuer || '—')}</dd>
              </div>
              <div>
                <dt>Связь</dt>
                <dd>{String(preview.linkedTo || '—')}</dd>
              </div>
            </dl>
            <p>{String(preview.description || 'Описание отсутствует.')}</p>
            {isDataImage ? (
              <img src={safeLink} alt="" className="profile-safe-preview-media" />
            ) : isHttp ? (
              <iframe
                title="Document preview"
                src={safeLink}
                sandbox=""
                referrerPolicy="no-referrer"
                className="profile-safe-preview-media"
              />
            ) : safeLink ? (
              <code className="profile-safe-preview-ref">{safeLink}</code>
            ) : (
              <span className="profile-subnote">Ссылка на файл не указана — показаны только метаданные.</span>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function CompletionPane({ profile, setField }: Props) {
  const dc = (profile.doctoral_completion || {}) as Record<string, string>;
  return (
    <div>
      <section className="profile-defense-line">
        <article className="done">
          <span>1</span>
          <div>
            <b>Допуск / предзащита</b>
            <small>Этап подготовки</small>
          </div>
        </article>
        <i />
        <article className={dc.defense_date ? 'done' : ''}>
          <span>2</span>
          <div>
            <b>Защита</b>
            <small>{dc.defense_date || 'Дата не указана'}</small>
          </div>
        </article>
        <i />
        <article className={dc.degree_decision_number ? 'done' : ''}>
          <span>3</span>
          <div>
            <b>Решение / приказ</b>
            <small>{dc.degree_decision_number || 'Ожидается'}</small>
          </div>
        </article>
        <i />
        <article className={dc.diploma_issue_date ? 'done' : ''}>
          <span>4</span>
          <div>
            <b>Диплом</b>
            <small>{dc.diploma_issue_date || 'Не выдан'}</small>
          </div>
        </article>
      </section>
      <div className="profile-form-grid" style={{ marginTop: 14 }}>
        <article className="profile-side-card">
          <h3>Защита и решение</h3>
          <Field
            label="Дата защиты"
            type="date"
            value={dc.defense_date || ''}
            onChange={(v) => setField('doctoral_completion.defense_date', v)}
          />
          <Field
            label="Решение / приказ"
            value={dc.degree_decision_number || ''}
            onChange={(v) => setField('doctoral_completion.degree_decision_number', v)}
          />
          <Field
            label="Дата решения"
            type="date"
            value={dc.degree_decision_date || ''}
            onChange={(v) => setField('doctoral_completion.degree_decision_date', v)}
          />
          <Field
            label="Серия / номер диплома"
            value={dc.diploma_series_number || ''}
            onChange={(v) => setField('doctoral_completion.diploma_series_number', v)}
          />
          <Field
            label="Дата выдачи диплома"
            type="date"
            value={dc.diploma_issue_date || ''}
            onChange={(v) => setField('doctoral_completion.diploma_issue_date', v)}
          />
          <Field
            label="Employment"
            value={dc.employment || ''}
            onChange={(v) => setField('doctoral_completion.employment', v)}
          />
        </article>
        <article className="profile-side-card">
          <h3>Публичные подтверждения</h3>
          <Field
            label="Объявление · URL"
            type="url"
            value={dc.defense_announcement_url || ''}
            onChange={(v) => setField('doctoral_completion.defense_announcement_url', v)}
          />
          <Field
            label="Отчёт на сайте · URL"
            type="url"
            value={dc.ino_site_report_url || ''}
            onChange={(v) => setField('doctoral_completion.ino_site_report_url', v)}
          />
          <Field
            label="Instagram · URL"
            type="url"
            value={dc.department_instagram_url || ''}
            onChange={(v) => setField('doctoral_completion.department_instagram_url', v)}
          />
          <p className="profile-subnote">
            Сведения профиля также используются при мониторинге обучения в докторантуре.
          </p>
        </article>
      </div>
    </div>
  );
}

function SettingsPane({ profile, setField }: Props) {
  const navigate = useNavigate();
  const privacy = (profile.privacy || {}) as Record<string, boolean>;
  const academiaId = String((profile.account as { academiaId?: string } | undefined)?.academiaId || '');
  return (
    <section className="profile-settings-grid">
      <article className="profile-settings-card is-privacy">
        <span className="profile-settings-icon" aria-hidden="true">
          🔒
        </span>
        <div className="profile-settings-copy">
          <h3>Privacy</h3>
          <p>Какие поля видны в публичном академическом профиле.</p>
        </div>
        <div className="profile-privacy-list">
          <label className="profile-check">
            <input
              type="checkbox"
              checked={Boolean(privacy.publicProfile)}
              onChange={(e) => setField('privacy.publicProfile', e.target.checked)}
            />
            <span>Включить публичный профиль</span>
          </label>
          {(
            [
              ['showAcademicIds', 'Научные ID', true],
              ['showPublications', 'Публикации', true],
              ['showEducation', 'Образование', true],
              ['showEmployment', 'Место работы', false],
              ['showProjects', 'Проекты', false],
            ] as const
          ).map(([key, label, defaultOn]) => (
            <label key={key} className="profile-check">
              <input
                type="checkbox"
                checked={privacy[key] === undefined ? defaultOn : Boolean(privacy[key])}
                disabled={!privacy.publicProfile}
                onChange={(e) => setField(`privacy.${key}`, e.target.checked)}
              />
              <span>{label}</span>
            </label>
          ))}
        </div>
        {privacy.publicProfile && academiaId ? (
          <button type="button" onClick={() => navigate(routes.publicProfile(academiaId))}>
            Открыть публичную страницу
          </button>
        ) : null}
      </article>

      <article className="profile-settings-card">
        <span className="profile-settings-icon" aria-hidden="true">
          🛡
        </span>
        <div className="profile-settings-copy">
          <h3>Двухэтапная аутентификация</h3>
          <p>Authenticator, резервные коды и доверенные устройства.</p>
        </div>
        <button type="button" onClick={() => navigate(routes.security)}>
          Настроить
        </button>
      </article>

      <article className="profile-settings-card">
        <span className="profile-settings-icon" aria-hidden="true">
          💻
        </span>
        <div className="profile-settings-copy">
          <h3>Активные сессии</h3>
          <p>Список устройств и возможность завершить другие сессии.</p>
        </div>
        <button type="button" onClick={() => navigate(routes.security)}>
          Открыть
        </button>
      </article>

      <article className="profile-settings-card">
        <span className="profile-settings-icon" aria-hidden="true">
          📦
        </span>
        <div className="profile-settings-copy">
          <h3>Экспорт данных</h3>
          <p>Полный снимок профиля (отдельно от CV / научных трудов).</p>
        </div>
        <button type="button" onClick={() => navigate(routes.profileExport('data'))}>
          Подготовить экспорт данных
        </button>
      </article>

      <article className="profile-settings-card">
        <span className="profile-settings-icon" aria-hidden="true">
          🔔
        </span>
        <div className="profile-settings-copy">
          <h3>Уведомления</h3>
          <p>Email, системные события, дедлайны и напоминания.</p>
        </div>
        <button
          type="button"
          onClick={() =>
            navigate(routes.notifications, {
              state: { returnTo: routes.profileSection('settings') },
            })
          }
        >
          Настроить
        </button>
      </article>

      <article className="profile-settings-card">
        <span className="profile-settings-icon" aria-hidden="true">
          🧾
        </span>
        <div className="profile-settings-copy">
          <h3>Audit</h3>
          <p>Критические изменения профиля и разрешений.</p>
        </div>
        <button type="button" onClick={() => navigate(routes.audit)}>
          Посмотреть
        </button>
      </article>
    </section>
  );
}

export function ProfileSectionBody(props: Props) {
  switch (props.sectionId) {
    case 'account':
      return <AccountPane {...props} />;
    case 'personal':
      return <PersonalPane {...props} />;
    case 'academic':
      return <AcademicPane {...props} />;
    case 'education':
      return <EducationPane {...props} />;
    case 'employment':
      return <EmploymentPane {...props} />;
    case 'publications':
      return <PublicationsPane {...props} />;
    case 'projects':
      return <ProjectsPane {...props} />;
    case 'achievements':
      return <AchievementsPane {...props} />;
    case 'documents':
      return <DocumentsPane {...props} />;
    case 'completion':
      return <CompletionPane {...props} />;
    case 'settings':
      return <SettingsPane {...props} />;
    default:
      return null;
  }
}
